/**
 * Artifact Types
 *
 * TypeScript types for Leo Artifacts - structured XML references to internal resources.
 *
 * Artifacts are the canonical way to reference campaigns, ad sets, ads, assets, and reports.
 * Every artifact must be grounded - Leo cannot fabricate IDs. Each artifact must reference
 * data from a tool call.
 */

import { Platform } from '@enums/Platform';

/**
 * MIME type for artifact drag data.
 * Used to identify artifact drops vs file drops.
 */
export const ARTIFACT_DRAG_MIME_TYPE = 'application/x-leo-artifact';

export type AdsEntityType = 'campaign' | 'adset' | 'ad';

/** Narrow an arbitrary string to the campaign/adset/ad set — the one membership
 *  test for "is this an ads-entity artifact", so callers don't re-spell the union. */
export const isAdsEntityType = (t: string): t is AdsEntityType =>
  t === 'campaign' || t === 'adset' || t === 'ad';
export type ReportsEntityType = 'report';
export type AssetsEntityType = 'asset';
export type FilesEntityType = 'file';
export type ScheduledWorkEntityType = 'scheduledWork';
export type BatchPlanEntityType = 'batch_plan';
export type ObjectiveEntityType = 'objective';
export type MonitorEntityType = 'monitor';
export type ConversationEntityType = 'conversation';
export type MemoEntityType = 'memo';
export type CreativesDashboardEntityType = 'creativesDashboard';
export type AnalyticsDashboardEntityType = 'analyticsDashboard';
export type LeadAttributionDashboardEntityType = 'leadAttribution';
export type GalleryDashboardEntityType = 'galleryDashboard';
export type MonitorsDashboardEntityType = 'monitorsDashboard';
/** A Meta instant (lead-gen) form — picked through the disambiguation picker and sent
 *  back as a reference. It has no tab, so its chip is not clickable. */
export type LeadFormEntityType = 'lead_form';
/** Standalone type for PickerArtifact.type — deliberately NOT part of ArtifactType. */
export type PickerEntityType = 'picker';
/**
 * Valid artifact types - matches backend ArtifactType enum.
 *
 * Note: ``picker`` is deliberately excluded from this union. Picker is a
 * *payload* artifact with a different shape (see PickerArtifact) and lives on
 * its own PickerEntityType so callers of ArtifactType (Artifact, TabPane,
 * EntityChip, ArtifactPicker, etc.) never have to handle it. MarkdownContent
 * checks ``attrs.type === 'picker'`` as a string literal upstream and dispatches
 * to DisambiguationPicker before the ArtifactType narrowing happens.
 */
export type ArtifactType =
  | AdsEntityType
  | ReportsEntityType
  | AssetsEntityType
  | FilesEntityType
  | ScheduledWorkEntityType
  | BatchPlanEntityType
  | ObjectiveEntityType
  | MonitorEntityType
  | ConversationEntityType
  | MemoEntityType
  | CreativesDashboardEntityType
  | AnalyticsDashboardEntityType
  | LeadAttributionDashboardEntityType
  | GalleryDashboardEntityType
  | MonitorsDashboardEntityType
  | LeadFormEntityType
  | 'campaignTimeline';

export type NonBatchPlanArtifactType = Exclude<ArtifactType, 'batch_plan'>;

export const VALID_ARTIFACT_TYPES: ArtifactType[] = [
  'campaign',
  'adset',
  'ad',
  'asset',
  'file',
  'report',
  'scheduledWork',
  'batch_plan',
  'objective',
  'monitor',
  'conversation',
  'memo',
  // A dashboard is a draggable reference like any other entity: its tab drags
  // into chat, its sidebar row drags into chat, and Leo can emit it as a chip
  // that opens the tab. BrowserTabs sets the artifact data unconditionally, so
  // a dashboard missing from this list was rejected by the chat's drop handler
  // SILENTLY — which is what "dragging the Performance tab does nothing" was.
  // Every dashboard belongs here; add the row when you add the dashboard.
  'creativesDashboard',
  'analyticsDashboard',
  'leadAttribution',
  'galleryDashboard',
  'monitorsDashboard',
  'campaignTimeline',
  // A picker selection comes back as `<artifact type="lead_form" …/>`; a type missing
  // here is dropped by the parser, so the user's own pick would vanish from the chat.
  'lead_form',
];

/** A chip's type and id as they are now. The Analytics dashboard was the Performance tab, and a chip
 * saved before the rename — in a message, a memo, an executive result — opens it. */
export const canonicalArtifactRef = (type: string | undefined, id: string | undefined) =>
  type === 'performanceDashboard' ? { type: 'analyticsDashboard', id: 'analytics' } : { type, id };

/** Types that name a real thing but have no tab to open — their chips render inert. */
export const isNonNavigableArtifactType = (t: string): t is LeadFormEntityType =>
  t === 'lead_form';

/** Home destinations have no canvas tab and never carry a tenant override.
 * Conversation syntax matches the existing backend conversation reader. The
 * authenticated chat/feed readers remain responsible for access control. */
export function getHomeArtifactTarget(type: string, id: string):
  | { kind: 'conversation'; href: string }
  | { kind: 'memo'; focusKey: string }
  | null {
  if (!id || /[^A-Za-z0-9_-]/.test(id)) return null;
  if (type === 'conversation' && id.length <= 155 &&
    /^(?:(?:conv_|cont_|async_)[A-Za-z0-9_-]+|planning_conv_[0-9]+_[a-f0-9]{8}|monitor_[a-f0-9]{32}|exec__[a-z0-9_]{1,64})$/.test(id)) {
    return { kind: 'conversation', href: `/?conversation_id=${encodeURIComponent(id)}` };
  }
  if (type === 'memo' && id.length <= 128) {
    return { kind: 'memo', focusKey: `post:${id}` };
  }
  return null;
}

export interface BaseArtifact {
  type: NonBatchPlanArtifactType;
  /** Canonical identifier (invariant, from tool output) */
  id: string;
  /** Display name (snapshot, may be stale) */
  name: string;
  /** Platform identifier (e.g., 'meta') - required for campaign/adset/ad */
  platform?: string;
  /** The tool that provided this ID (e.g., 'get_campaigns', 'create_ad', 'user_input') */
  groundedBy: string;
  /** Thumbnail URL for asset display */
  thumbnail?: string;
  /** Asset type (image, video) for proper thumbnail handling */
  assetType?: string;
  /** Processing quality retained when a fresh upload becomes an attachment chip. */
  analysisQuality?: 'limited';
}

export interface BatchPlanArtifact {
  type: 'batch_plan';
  id: string;
  name: string;
  platform?: string;
  groundedBy?: string;
  /** Number of batches (batch_plan only) */
  batches?: string;
  /** Total number of ads (batch_plan only) */
  totalAds?: string;
}

/**
 * One selectable row inside a PickerArtifact.
 *
 * Only ``id`` and ``name`` are structural; ``platform`` is broken out because
 * the shared PlatformIcon renderer needs it. Everything else the backend emits
 * as an XML attribute (``status``, ``budget``, ``thumbnail_url``, ``dimensions``,
 * ``published_at``, whatever) is captured in ``fields`` — the type-specific
 * config in ``DisambiguationPicker``'s TYPE_CONFIGS registry decides which
 * fields to render for each artifact type. Adding a new type = one registry
 * entry, no changes here.
 *
 * Keys are the raw XML attribute names (snake_case, matching the backend).
 */
export interface PickerCandidate {
  /** Canonical identifier of the underlying artifact */
  id: string;
  /** Display name */
  name: string;
  /** Platform (e.g., 'meta') — for platform-scoped artifact types */
  platform?: string;
  /** All other XML attributes, keyed by the raw attribute name (snake_case) */
  fields: Record<string, string>;
}

/**
 * A disambiguation picker payload. Rendered as an interactive widget in the
 * chat panel. Emitted by the `show_disambiguation_picker` tool when Leo needs
 * to resolve which artifact the user is referring to.
 */
export interface PickerArtifact {
  type: 'picker';
  /** Short question shown above the picker (e.g., "Which Meta campaign?") */
  prompt: string;
  /** The artifact type each candidate refers to (campaign, asset, etc.) */
  artifactType: NonBatchPlanArtifactType;
  /** single (radios) or multi (checkboxes) */
  selectMode: 'single' | 'multi';
  /** Ranked candidate list (top-N) */
  candidates: PickerCandidate[];
  /** The tool that emitted this picker */
  groundedBy: string;
}

/**
 * The reference-artifact union used everywhere in the app to reference platform
 * entities (campaigns, ads, creatives, reports, etc). Does NOT include
 * ``PickerArtifact`` — the picker is a *payload* artifact with a different shape
 * (nested candidates, no ``id``/``name`` at the top level). MarkdownContent parses
 * picker containers directly from the DOM tree via html-react-parser; a picker's
 * opening tag DOES reach ``findArtifacts``, which skips it explicitly, so both
 * ``parseArtifactXml`` and ``findArtifacts`` yield only reference artifacts.
 */
export type Artifact = BaseArtifact | BatchPlanArtifact;


/**
 * Parse a single artifact XML string into an Artifact object.
 *
 * @param xmlString - The artifact XML string to parse
 * @returns The parsed Artifact, or null if parsing fails
 *
 * @example
 * parseArtifactXml('<artifact type="campaign" id="123" platform="meta" name="Summer Sale" grounded_by="get_campaigns"/>')
 * // Returns: { type: 'campaign', id: '123', platform: 'meta', name: 'Summer Sale', groundedBy: 'get_campaigns' }
 */
export function parseArtifactXml(xmlString: string): Artifact | null {

  // Extract attributes string. The closing slash is OPTIONAL: a model that writes
  // `<artifact type="asset" …>` instead of `…/>` has still named a real, grounded entity, and
  // dropping the chip over one character loses the asset with no error the user or we can see.
  // Reference artifacts carry no inner content, so there is nothing to lose by ignoring it.
  const match = /<artifact\s+([^>]*?)\/?>/i.exec(xmlString);
  if (!match) return null;

  const attrsString = match[1];
  const attrs: Record<string, string> = {};

  // Parse all attributes
  let attrMatch: RegExpExecArray | null;
  const attrRegex = /(\w+)="([^"]*)"/g;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    attrs[attrMatch[1]] = attrMatch[2];
  }

  // Normalize platform at the parse boundary so every consumer matches
  // regardless of how the model or OAuth layer spelled it ("LINKEDIN",
  // "google_ads", "bing-ads", …). Maps to the canonical lowercase Platform key.
  if (attrs.platform) {
    attrs.platform = normalizeArtifactPlatform(attrs.platform) ?? attrs.platform;
  }
  const ref = canonicalArtifactRef(attrs.type, attrs.id);
  if (ref.type) attrs.type = ref.type;
  if (ref.id) attrs.id = ref.id;

  // Validate required fields
  const type = attrs.type as ArtifactType | undefined;
  const id = attrs.id;
  const name = attrs.name;
  const groundedBy = attrs.grounded_by;

  // batch_plan artifacts can omit grounded_by; they use 'batches' and 'total_ads'
  if (type === 'batch_plan') {
    if (!id || !name) {
      console.warn('Invalid batch_plan artifact XML - missing id or name:', xmlString);
      return null;
    }
  } else if (!type || !id || !name || !groundedBy) {
    console.warn('Invalid artifact XML - missing required attributes:', xmlString);
    return null;
  }

  // Validate type is one of the allowed values
  if (!VALID_ARTIFACT_TYPES.includes(type)) {
    console.warn('Invalid artifact type:', type);
    return null;
  }

  if ((type === 'conversation' || type === 'memo') && !getHomeArtifactTarget(type, id)) return null;

  // HTML unescape the name
  const unescapedName = name
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Parse thumbnail URL (unescape HTML entities)
  const thumbnail = attrs.thumbnail
    ? attrs.thumbnail
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
    : undefined;

  if (type === 'batch_plan') {
    return {
      type: 'batch_plan',
      id,
      name: unescapedName,
      platform: attrs.platform,
      groundedBy: groundedBy,
      batches: attrs.batches,
      totalAds: attrs.total_ads,
    };
  }

  return {
    type,
    id,
    name: unescapedName,
    platform: attrs.platform,
    groundedBy: groundedBy,
    // Display-only properties for assets
    thumbnail,
    assetType: attrs.asset_type,
  };
}

/**
 * Result of finding artifacts in content, including their positions.
 */
export interface ArtifactMatch {
  artifact: Artifact;
  startIndex: number;
  endIndex: number;
  rawXml: string;
}

/**
 * Find all artifact XML tags in content and parse them.
 *
 * Matches a reference tag whether or not it closed itself — ``<artifact ... />`` and
 * ``<artifact ...>`` both parse, because the attributes are the whole payload of a reference.
 *
 * Payload cards (picker, confirm, and outcome activation) are NOT returned. A picker's opening
 * tag matches this regex, but it carries
 * ``prompt``/``artifact_type``/``select_mode`` in place of ``id``/``name`` (see
 * ``PickerArtifact.to_xml`` in ``schemas/artifact.py``), so ``parseArtifactXml`` would reject it
 * at the required-attribute check and warn on every picker the app renders. The skip below is
 * what keeps that warning out of the console. Pickers are parsed from the DOM tree by
 * ``MarkdownContent``, which owns their nested candidates.
 *
 * @param content - The content string to search
 * @returns Array of parsed artifacts with their positions
 */
export function findArtifacts(content: string): ArtifactMatch[] {
  const matches: ArtifactMatch[] = [];
  const regex = /<artifact\s+[^>]*?\/?>/gi;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    if (/\btype="(?:picker|confirm|outcome_activation)"/i.test(match[0])) continue;
    const artifact = parseArtifactXml(match[0]);
    if (artifact) {
      matches.push({
        artifact,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        rawXml: match[0],
      });
    }
  }

  return matches;
}

/**
 * Serialize an Artifact to XML string format.
 *
 * @param artifact - The artifact to serialize
 * @returns XML string representation
 *
 * @example
 * artifactToXml({ type: 'campaign', id: '123', platform: 'meta', name: 'Summer Sale', groundedBy: 'user_input' })
 * // Returns: '<artifact type="campaign" id="123" platform="meta" name="Summer Sale" grounded_by="user_input"/>'
 */
export function artifactToXml(artifact: Artifact, options: { includeThumbnail?: boolean } = {}): string {
  // HTML escape the name for safety
  const safeName = artifact.name
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const attrs: string[] = [
    `type="${artifact.type}"`,
    `id="${artifact.id}"`,
  ];

  if (artifact.platform) {
    attrs.push(`platform="${artifact.platform}"`);
  }

  attrs.push(`name="${safeName}"`);
  if (artifact.groundedBy) {
    attrs.push(`grounded_by="${artifact.groundedBy}"`);
  }

  if (artifact.type === 'batch_plan') {
    if (artifact.batches) {
      attrs.push(`batches="${artifact.batches}"`);
    }
    if (artifact.totalAds) {
      attrs.push(`total_ads="${artifact.totalAds}"`);
    }
  }

  // Include thumbnail and assetType for asset types (used for hover preview)
  if (artifact.type === 'asset') {
    if (artifact.thumbnail && options.includeThumbnail !== false) {
      // URL-encode the thumbnail to safely include it in the attribute
      const safeThumbnail = artifact.thumbnail
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;');
      attrs.push(`thumbnail="${safeThumbnail}"`);
    }
    if (artifact.assetType) {
      attrs.push(`asset_type="${artifact.assetType}"`);
    }
  }

  return `<artifact ${attrs.join(' ')}/>`;
}

/**
 * Convert platform string to Platform enum.
 * Returns undefined if platform is not recognized.
 */
export function artifactPlatformToPlatformEnum(platform?: string): Platform | undefined {
  if (!platform) return undefined;

  // Canonicalize separators + case before matching: models and the OAuth layer
  // spell platforms several ways — "LINKEDIN", "google_ads", "google-ads",
  // "bing_ads". Collapse dashes to underscores and lowercase so one set of
  // cases covers them all.
  const normalized = platform.toLowerCase().replace(/-/g, '_');
  switch (normalized) {
    case 'meta':
    case 'facebook':
    case 'instagram':
      return Platform.META;
    case 'google':
    case 'google_ads':
    case 'googleads':
    case 'google_analytics':
      return Platform.GOOGLE;
    case 'linkedin':
      return Platform.LINKEDIN;
    case 'tiktok':
    case 'tik_tok':
      return Platform.TIKTOK;
    case 'reddit':
      return Platform.REDDIT;
    case 'bing':
    case 'bing_ads':
    case 'microsoft':
      return Platform.BING;
    case 'amazon':
    case 'amazon_ads':
      return Platform.AMAZON;
    case 'shopify':
      return Platform.SHOPIFY;
    default:
      return undefined;
  }
}

/**
 * Normalize an artifact's raw platform string to the canonical Platform key
 * the frontend dispatches on (lowercase, no underscores/dashes variance).
 * Use this at the artifact→tab boundary so every downstream consumer — chips,
 * tab dispatch, breadcrumb maps — matches regardless of how the model or the
 * OAuth layer spelled the platform. Falls back to a lowercased value for
 * platforms we don't explicitly map, so nothing is dropped.
 */
export function normalizeArtifactPlatform(platform?: string): string | undefined {
  if (!platform) return undefined;
  return artifactPlatformToPlatformEnum(platform) ?? platform.toLowerCase().replace(/-/g, '_');
}

/**
 * Check if an artifact type requires a platform.
 */
export function artifactTypeRequiresPlatform(type: ArtifactType): boolean {
  return type === 'campaign' || type === 'adset' || type === 'ad';
}

const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'pptx', 'xlsx'];
const DOCUMENT_MIME_PREFIXES = ['application/pdf', 'application/vnd.openxmlformats'];

/**
 * Determine if an asset is a document (PDF, Word, Excel, PowerPoint).
 */
function isDocumentAsset(mimeType?: string, url?: string, name?: string): boolean {
  if (mimeType && DOCUMENT_MIME_PREFIXES.some(p => mimeType.startsWith(p))) {
    return true;
  }
  const source = url || name || '';
  const ext = source.split('?')[0].split('.').pop()?.toLowerCase();
  return DOCUMENT_EXTENSIONS.includes(ext || '');
}

/**
 * Determine if an asset is a video based on MIME type or URL extension.
 */
function isVideoAsset(mimeType?: string, url?: string): boolean {
  if (mimeType?.startsWith('video/')) {
    return true;
  }
  if (url) {
    const extension = url.split('.').pop()?.toLowerCase();
    return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'].includes(extension || '');
  }
  return false;
}

/**
 * Convert an Asset to an Artifact for use in chat.
 * The asset_id becomes the artifact id, enabling Leo to reference the asset.
 *
 * @param asset - The asset to convert (must have asset_id)
 * @returns An Artifact with type='asset' and display properties for thumbnail
 */
export function assetToArtifact(asset: {
  asset_id: string;
  asset_name?: string | null;
  asset_original_name?: string | null;
  asset_mime_type?: string | null;
  url?: string | null;
  s3_path_to_thumbnail?: string | null;
  analysis_quality?: 'limited';
}): Artifact {
  const name = asset.asset_original_name || asset.asset_name || 'Asset';
  const mimeType = asset.asset_mime_type ?? undefined;
  const url = asset.url ?? undefined;

  // Route documents (PDF, Word, Excel, PowerPoint) to 'file' type
  if (isDocumentAsset(mimeType, url, name)) {
    return {
      type: 'file',
      id: asset.asset_id,
      name,
      groundedBy: 'user_input',
    };
  }

  return {
    type: 'asset',
    id: asset.asset_id,
    name,
    groundedBy: 'user_input',
    // Display-only properties
    thumbnail: asset.s3_path_to_thumbnail || asset.url || undefined,
    assetType: isVideoAsset(mimeType, url) ? 'video' : 'image',
    ...(asset.analysis_quality === 'limited' ? { analysisQuality: 'limited' as const } : {}),
  };
}
